/**
 * UnderwaterShafts — light shafts seen from under the surface (shader wave
 * stage 4, 2026-09-09). Crepuscular rays underwater are photons out-scattered
 * from a sun beam by the water's particles (Papadopoulos & Papaioannou 2009,
 * "Realistic Real-time Underwater Caustics and Godrays"); the cheap classic
 * that reads right from any camera is a fan of additive VERTICAL SHEETS around
 * the viewer, each carrying scrolling noise streaks aligned with the sun's
 * slant, fading with depth below the surface (the beams die out with the
 * light) and toward their foot. No screen-space pass, no depth read — a mesh
 * the water module parents to the camera while submerged.
 *
 * `shaftDepthFadeCpu` and `shaftStreakCpu` are the oracles.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../../render/post/mrtOverlay.js';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, time, attribute, sin, fract, smoothstep, saturate, mx_noise_float,
} from 'three/tsl';

export interface UnderwaterShaftsConfig {
  /** Sheets in the fan (default 14). */
  count?: number;
  /** Sheet height (m, default 40) and width (m, default 7). */
  length?: number;
  width?: number;
  /** Ring radius the sheets stand on (m, default 12). */
  radius?: number;
  /** Beam colour (default a pale aqua). */
  color?: [number, number, number];
  /** Intensity (default 1). */
  intensity?: number;
  /** Depth (m) at which the beams have faded to e⁻¹ (default 14). */
  depthScale?: number;
}

export interface UnderwaterShaftsUniforms {
  intensity: { value: number };
  /** Camera depth below the surface (m) — the caller writes it each frame. */
  depth: { value: number };
  /** Sun slant: horizontal offset of the beam foot per metre of height. */
  slant: { value: THREE.Vector2 };
}

/** Beam brightness by depth below the surface. */
export function shaftDepthFadeCpu(depth: number, depthScale = 14): number {
  if (!(depth >= 0)) return 0;
  return Math.exp(-depth / depthScale);
}

/** Streak mask across a sheet at u (0..1) with per-sheet phase and time. */
export function shaftStreakCpu(u: number, phase: number, t: number): number {
  const x = u * 5 + phase * 7 + t * 0.12;
  const bands = Math.pow(Math.max(0, Math.sin(x * Math.PI * 2) * 0.5 + 0.5), 3);
  const edge = Math.sin(u * Math.PI);
  return bands * edge;
}

export function createUnderwaterShafts(config?: UnderwaterShaftsConfig): { mesh: THREE.Mesh; uniforms: UnderwaterShaftsUniforms } {
  const count = Math.max(4, Math.min(48, config?.count ?? 14));
  const len = config?.length ?? 40, width = config?.width ?? 7, radius = config?.radius ?? 12;
  const col = config?.color ?? [0.55, 0.85, 0.95];
  const depthScale = config?.depthScale ?? 14;
  const uIntensity = uniform(config?.intensity ?? 1);
  const uDepth = uniform(0);
  const uSlant = uniform(new THREE.Vector2(0.25, 0.1));

  // Geometry: `count` quads on a ring, each facing the ring centre, top at +len/2.
  const pos = new Float32Array(count * 4 * 3), uvs = new Float32Array(count * 4 * 2), rand = new Float32Array(count * 4);
  const idx: number[] = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + (i % 2) * 0.13;
    const cx = Math.cos(a) * radius, cz = Math.sin(a) * radius;
    const tx = -Math.sin(a) * width * 0.5, tz = Math.cos(a) * width * 0.5;
    const r = ((i * 7919) % 1000) / 1000;
    const corners = [[cx - tx, -len / 2, cz - tz, 0, 0], [cx + tx, -len / 2, cz + tz, 1, 0], [cx + tx, len / 2, cz + tz, 1, 1], [cx - tx, len / 2, cz - tz, 0, 1]];
    for (let k = 0; k < 4; k++) {
      const v = i * 4 + k;
      pos[v * 3] = corners[k][0]; pos[v * 3 + 1] = corners[k][1]; pos[v * 3 + 2] = corners[k][2];
      uvs[v * 2] = corners[k][3]; uvs[v * 2 + 1] = corners[k][4];
      rand[v] = r;
    }
    idx.push(i * 4, i * 4 + 1, i * 4 + 2, i * 4, i * 4 + 2, i * 4 + 3);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geo.setAttribute('aRand', new THREE.BufferAttribute(rand, 1));
  geo.setIndex(idx);
  geo.computeBoundingSphere();

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.depthTest = false;
  material.side = THREE.DoubleSide;
  material.blending = THREE.AdditiveBlending;
  material.fog = false;
  markMrtOverlay(material); // additive light sheets over the seabed

  material.colorNode = Fn(() => {
    const u0 = uv();
    const phase = attribute('aRand', 'float');
    // slant: shift the streak across the sheet with height (the sun's angle)
    const shear = u0.y.mul(uSlant.x).add(u0.x);
    const x = shear.mul(5).add(phase.mul(7)).add(time.mul(0.12));
    const bands = saturate(sin(x.mul(Math.PI * 2)).mul(0.5).add(0.5)).pow(3);
    const grain = mx_noise_float(vec2(x.mul(1.7), u0.y.mul(3).add(time.mul(0.05)))).mul(0.5).add(0.5);
    const edge = sin(u0.x.mul(Math.PI));
    // brighter toward the surface, gone at the foot
    const vert = smoothstep(float(0), float(0.35), u0.y).mul(u0.y.pow(1.5));
    const depthFade = uDepth.div(depthScale).negate().exp();
    const v = bands.mul(grain).mul(edge).mul(vert).mul(depthFade).mul(uIntensity).mul(0.35);
    return vec4(vec3(col[0], col[1], col[2]).mul(v), 1);
  })();

  const mesh = new THREE.Mesh(geo, material);
  mesh.name = 'underwater-shafts';
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return { mesh, uniforms: { intensity: uIntensity as unknown as { value: number }, depth: uDepth as unknown as { value: number }, slant: uSlant as unknown as { value: THREE.Vector2 } } };
}

export const _underwaterShaftsRefs = { fract };
